import Copy from "@components/global/new/Copy/Copy";
import SmallLink from "@components/global/new/SmallLink/SmallLink";
import cn from "classnames";
import { useEffect, useRef } from "react";
import ReactCardFlip from "react-card-flip";
import styles from "./RenameBuilding.module.scss";

import origCopy from "@assets/redesign/icons/copy.svg";
import wlpCopy from "@components/WLP202603/assets/redesign/icons/copy.svg";
import { WLP202603 } from "../../../config";
import TranslitingTooltip from "@components/global/new/TranslitingTooltip/TranslitingTooltip";
import { useWatchNetwork } from "@mobx/services/http";
import mainLocationsStore from "@mobx/store/locations";
import { useTransliting } from "@utils/translitingText";
import { observer } from "mobx-react-lite";
import { useState } from "react";
import { useIntl } from "react-intl";

export interface IRenameBuilding3Props {}
export type EventKeyTypes3 = "Enter" | "Escape";

const copy = WLP202603 ? wlpCopy : origCopy;

const useControl = (
  props: IRenameBuilding3Props
) => {
  const locationStore = mainLocationsStore.activeLocation;
  const buildingStore = locationStore?.activeBuilding;
  const {
    ref: tooltipRef,
    getRef: getTooltipRef,
    onTranslit: translit,
  } = useTransliting();
  const [changedName, setChangedName] = useState<string>(
    buildingStore?.building.name || ""
  );
  const [isEdit, setIsEdit] = useState<boolean>(false);
  useWatchNetwork(buildingStore);

  const onEdit = () => setIsEdit(true);

  const onChangeName = (v: string) => {
    const value = translit(v);
    if (value !== v) tooltipRef.current?.onOpen();
    setChangedName(value);
  };

  const onSubmitChanges = async (key: EventKeyTypes3) => {
    if (!buildingStore?.building) return;
    switch (key) {
      case "Enter":
        await buildingStore.rename(changedName);
        setIsEdit(false);
        break;
      case "Escape":
        setChangedName(buildingStore.building.name || "");
        setIsEdit(false);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (buildingStore?.building.name === changedName) return;
    if (!buildingStore?.building) return;
    setChangedName(buildingStore?.building.name);
  }, [buildingStore?.building.name]);

  return {
    changedName,
    isEdit,
    activeBuilding: buildingStore?.building,

    getTooltipRef,
    onEdit,
    onChangeName,
    onSubmitChanges,
  };
};

const RenameBuilding3 = observer((props: IRenameBuilding3Props) => {
  const { formatMessage } = useIntl();
  const ref = useRef<HTMLInputElement>(null);
  const control = useControl(props);

  useEffect(() => {
    if (!control.isEdit) return;

    ref.current?.focus();
  }, [control.isEdit]);

  return (
    <div className={styles.wrapper} onDoubleClick={control.onEdit}>
      <p>{formatMessage({ id: "location.building" })}</p>
      <ReactCardFlip flipDirection='vertical' isFlipped={control.isEdit}>
        <span title={`Building ID (#${control.changedName})`}>
          {control.changedName}
        </span>
        <div className={styles.input_wrapper}>
          <div className={styles.input_field}>
            <TranslitingTooltip controlledRef={control.getTooltipRef}>
              <input
                type='text'
                onChange={(e) => control.onChangeName(e.target.value)}
                onKeyDown={(e) =>
                  control.onSubmitChanges(e.key as EventKeyTypes3)
                }
                value={control.changedName}
                ref={ref}
              />
            </TranslitingTooltip>
            {control.changedName !== control.activeBuilding?.name && (
              <div
                className={styles.submit}
                onClick={() => control.onSubmitChanges("Enter")}
              />
            )}
          </div>
          {control.isEdit && (
            <SmallLink
              className={styles.cancel}
              onClick={() => control.onSubmitChanges("Escape")}
            >
              {formatMessage({ id: "location.building.info.cancel" })}
            </SmallLink>
          )}
        </div>
      </ReactCardFlip>
      {!control.isEdit && (
        <Copy
          text={control.activeBuilding?.name || ""}
          icon={copy}
          className={cn(styles.copy_icon)}
        />
      )}
    </div>
  );
});

RenameBuilding3.displayName = "RenameBuilding3";

export default RenameBuilding3;
